import React, { useState, useId } from "react";
import { ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";

function AccordionItem({ id, title, open, onToggle, children }) {
  return (
    <div className="rounded-xl overflow-hidden border border-[#5986bd]/30">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={`panel-${id}`}
        className="relative w-full flex items-center justify-center font-semibold
                   px-4 py-3 bg-[#5986bd] hover:bg-[#4f78a8] text-white"
      >
        {/* Título centrado */}
        <span className="text-center">{title}</span>

        {/* Chevron a la derecha */}
        <ChevronDown
          className={`absolute right-4 transition-transform text-white ${open ? "rotate-180" : ""}`}
        />
      </button>

      <div
        id={`panel-${id}`}
        role="region"
        className={`grid transition-[grid-template-rows] duration-300 ease-in-out 
                    ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
      >
        <div className="overflow-hidden">
          {/* Mismo color, muy claro + texto justificado */}
          <div className="p-4 bg-[#5986bd]/10 text-gray-900 [text-align:justify] leading-relaxed">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FAQs() {
  const [openIndex, setOpenIndex] = useState(-1); // -1 = todas cerradas
  const baseId = useId();

  // 16 items
  const items = [
    {
      title: "No me gusta el agua espesada, ¿qué puedo darle?",
      description:
        "El agua se puede saborizar con cualquier zumo (gotas de limón y azúcar/sacarina, gotas de naranja), o bien saborizantes comerciales que se pueden comprar.",
    },
    {
      title: "¿A qué temperatura puedo tomar el agua espesada?",
      description:
        "Debe darse acorde con la temperatura ambiental/gustos del paciente, por ejemplo fresquita en verano (frigorífico).",
    },
    {
      title: "Una persona con disfagia ¿debe siempre comer triturado?",
      description:
        "No, la dieta de disfagia es individualizada, adaptada al grado de masticación y deglución del paciente.",
    },
    {
      title: "El paciente con disfagia ¿se cura?",
      description:
        "Depende de la causa. En AVC o TCE puede mejorar; en enfermedades neurodegenerativas suele progresar ligada a la enfermedad. Informar y adiestrar a la familia ayuda a prevenir complicaciones.",
    },
    {
      title: "Un paciente con disfagia ¿no puede tomar ninguna fruta carnosa?",
      description:
        "Sí, pero cambiando la textura. Por ejemplo, triturando la sandía, añadiendo espesante y tomándola como gelificada.",
    },
    {
      title: "Las gelatinas comerciales, ¿las puedo tomar si tengo disfagia?",
      description:
        "En general NO. Muchas gelatinas a >21°C pasan a estado líquido (riesgo) y no resisten la amilasa salival. Mejor agua gelificada casera con textura pudín según pauta.",
    },
    {
      title: "¿Se puede espesar el alcohol?",
      description:
        "Sí, siempre que retiremos previamente el gas (por ejemplo, de una cerveza).",
    },
    {
      title: "El agua con espesante ¿hidrata igual que el agua normal?",
      description:
        "Sí. Es la nueva forma de beber líquidos adaptada a su situación; las recomendaciones hídricas son las mismas con o sin espesante.",
    },
    {
      title: "¿Cuáles son los signos y síntomas de disfagia orofaríngea?",
      description: "Tos, disfonía o voz húmeda, carraspeo, regurgitación nasal…",
    },
    {
      title: "¿Cuáles son las complicaciones de la disfagia orofaríngea?",
      description: "Desnutrición, deshidratación, bronconeumonía por aspiración y atragantamiento.",
    },
    {
      title: "¿Cuánto espesante tengo que añadir al agua?",
      description:
        "Dependerá del volumen a espesar. Consulta la etiqueta del envase, que indica las cantidades.",
    },
    {
      title: "¿Pueden tener efectos secundarios los espesantes comerciales?",
      description:
        "Pueden causar saciedad precoz (si se abusa), estreñimiento, gases, heces blandas o diarrea. También reducir la biodisponibilidad de algunos fármacos: revisar medicación.",
    },
    {
      title: "¿Se puede utilizar espesante comercial para espesar suplementos nutricionales líquidos?",
      description:
        "Sí. Lo ideal es usar coctelera. La viscosidad deseada se alcanza a los 10–15 minutos. Evita añadir más espesante a una bebida ya espesada para no formar grumos.",
    },
    {
      title: "Los espesantes comerciales ¿engordan?",
      description:
        "No hay evidencia de que engorden. Su función es aumentar la viscosidad sin modificar sabor u olor.",
    },
  ];

  return (
    <div className="p-4 max-w-2xl mx-auto flex flex-col gap-4">
      {/* Encabezado */}
      <div className="flex items-center justify-center">
        <h3 className="font-bold text-center">PREGUNTAS Y RESPUESTAS FRECUENTES (FAQs)</h3>
      </div>

      {/* Acordeón controlado: solo una sección abierta */}
      <div className="mt-2 space-y-4">
        {items.map((it, idx) => (
          <AccordionItem
            key={idx}
            id={`${baseId}-${idx}`}
            title={it.title}
            open={openIndex === idx}
            onToggle={() => setOpenIndex(openIndex === idx ? -1 : idx)}
          >
            <p className="mb-0">{it.description}</p>
          </AccordionItem>
        ))}
      </div>

      {/* Formulario */}
      <div className="flex items-center">
        <p className="text-justify gap-4 mt-2 mb-4">
          Si desea realizar otra consulta, puede hacerlo a través de este{" "}
          <Link to="/faqs/formulario" className="text-blue-600 hover:underline">formulario</Link>.
        </p>
      </div>
    </div>
  );
}